import React, { useMemo } from 'react';
import FieldItem from '@/components/TextField/FieldItem';
import './index.scss';
import { translatorEnum } from '@/utils/util';
import {
  ChargeStateEnum,
  ChargerConnectEnum,
  DriveMode,
  VehicleStateEnum,
} from '@/utils/constant';
import { CockpitMode } from '@/utils/type';
import { QuestionCircleFilled } from '@ant-design/icons';
import { Col, Row, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { shallowEqual } from 'react-redux';

const StatusFields = (props: {
  vehicleState: VehicleStateEnum | DriveMode;
  delay: number;
  cockpitMode: CockpitMode;
  takeOverBySelf: boolean;
  chargeState: ChargeStateEnum;
  chargerConnect: ChargerConnectEnum;
  chargeLastRecordTime: string;
}) => {
  const {
    vehicleState,
    delay,
    cockpitMode,
    takeOverBySelf,
    chargeState,
    chargerConnect,
    chargeLastRecordTime,
  } = props;

  // --------useSelector取值区域--------
  const systemState = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.systemState
  );
  const info1 = useSelector(
    (state: RootState) =>
      state.realtime?.realtimeData?.signalConnectivityDelay?.[0],
    shallowEqual
  );
  const info2 = useSelector(
    (state: RootState) =>
      state.realtime?.realtimeData?.signalConnectivityDelay?.[1],
    shallowEqual
  );
  const arrivedMileage = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.arrivedMileage
  );
  const globalMileage = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.globalMileage
  );
  const scheduleState = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.scheduleState
  );
  const bindingState = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.bindingState
  );
  const driveOutMqtt1Online = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.driveOutMqtt1Online
  );
  const driveInMqtt1Online = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.driveInMqtt1Online
  );
  const driveOutMqtt2Online = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.driveOutMqtt2Online
  );
  const driveInMqtt2Online = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.driveInMqtt2Online
  );
  const power = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.power
  );
  // --------useSelector取值区域--------

  /**
   * 电量数值解析：将power转换为整数显示
   */
  const powerValue = useMemo(() => parseInt(power?.toString() || '0'), [power]);

  const statusText = useMemo(
    () => translatorEnum('SYSTEM_STATE_ENUM', systemState) ?? '--',
    [systemState]
  );

  // 充电状态展示逻辑
  const chargeInfo = useMemo(() => {
    const isOffline = statusText === '离线';
    const isConnected = chargerConnect === ChargerConnectEnum.CHARGER_CONNECT;
    const isCharging = chargeState === ChargeStateEnum.CHARGING;
    let text = '';
    let color = '';
    let title = '';
    let node = null;

    if (isCharging) {
      text = '充电中';
    } else if (isConnected) {
      text = '已插枪未充电';
    }

    if (isOffline) {
      color = '#999';
      title = `最后上报：${chargeLastRecordTime}`;
    }

    if (isCharging || isConnected) {
      node = (
        <div style={{ color, position: 'relative' }}>
          <span>{power}</span>
          <div
            style={{
              fontSize: '14px',
              marginLeft: '2px',
              position: 'absolute',
              width: '100px',
              bottom: '-12px',
            }}
          >
            {text}
          </div>
        </div>
      );
    }

    return { node, title };
  }, [chargeState, chargerConnect, power, statusText, chargeLastRecordTime]);
  const signalTip = (
    <div className="signal-tip">
      <Row>
        <Col style={{ marginRight: '20px' }}>
          <div style={{ borderBottom: '1px solid white' }}>
            卡1&nbsp;RSRP值&nbsp;SINR值
          </div>
          <div>卡2&nbsp;RSRP值&nbsp;SINR值</div>
        </Col>
        <Col>
          <div>
            网络状态稳定时，数值展示为<b style={{ color: 'green' }}>绿色</b>
          </div>
          <div>
            网络状态一般时，数值展示为<b style={{ color: 'orange' }}>橙色</b>
          </div>
          <div>
            网络状态极差时，数值展示为<b style={{ color: 'red' }}>红色</b>
          </div>
        </Col>
      </Row>
    </div>
  );

  const mqttTip = (
    <div className="signal-tip">
      <Row>
        <Col style={{ marginRight: '20px' }}>
          <div style={{ borderBottom: '1px solid white' }}>
            链路1&nbsp;车端接收状态&nbsp;车端上报状态
          </div>
          <div> 链路2&nbsp;车端接收状态&nbsp;车端上报状态</div>
        </Col>
        {/* <Col>
          <div>
            网络状态稳定时，数值展示为<b style={{ color: 'green' }}>绿色</b>
          </div>
          <div>
            网络状态一般时，数值展示为<b style={{ color: 'orange' }}>橙色</b>
          </div>
          <div>
            网络状态极差时，数值展示为<b style={{ color: 'red' }}>红色</b>
          </div>
        </Col> */}
      </Row>
    </div>
  );

  return (
    <div className="realtime-info">
      <div className="left-field-info">
        <FieldItem
          title="模式"
          info={
            vehicleState
              ? takeOverBySelf
                ? vehicleState
                : translatorEnum('VEHICLE_STATE_ENUM', vehicleState)
              : '--'
          }
          color={
            vehicleState === VehicleStateEnum.REMOTE_JOYSTICK_SUPER_CTRL ||
            vehicleState ===
              DriveMode.DRIVEMODE_TAKEOVER_REMOTE_JOYSTICK_SUPER_CTRL
              ? 'red'
              : undefined
          }
        />
        <FieldItem title="状态" info={statusText} />
        <FieldItem title="延时" info={delay} />
        {chargeInfo.title ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Tooltip
              styles={{
                body: {
                  minWidth: '300px',
                  fontSize: '18px',
                },
              }}
              title={chargeInfo.title}
            >
              <div>
                <FieldItem
                  style={{
                    lineHeight: 'unset',
                    minHeight: 'unset',
                  }}
                  title="电量"
                  info={chargeInfo.node ?? powerValue}
                />
              </div>
            </Tooltip>
          </div>
        ) : (
          <FieldItem title="电量" info={chargeInfo.node ?? powerValue} />
        )}
      </div>
      <div className="right-field-info">
        <FieldItem
          title={
            <div className="signal-title">
              信号
              <QuestionCircleFilled style={{ fontSize: '14px' }} />
            </div>
          }
          info={
            info1 || info2 ? (
              <Tooltip
                title={signalTip}
                placement="top"
                color="rgb(35 34 34 85%)"
                overlayStyle={{ maxWidth: 900, whiteSpace: 'normal' }}
              >
                <div className="signal-delay-info">
                  {info1 && (
                    <div>
                      <b
                        style={{
                          color:
                            info1.rsrpLevel === 'POOR'
                              ? 'red'
                              : info1.rsrpLevel === 'FAIR'
                                ? 'orange'
                                : 'green',
                        }}
                      >
                        {info1.rsrp.toFixed(2)}
                      </b>
                      &nbsp;
                      <b
                        style={{
                          color:
                            info1.sinrLevel === 'POOR'
                              ? 'red'
                              : info1.sinrLevel === 'FAIR'
                                ? 'orange'
                                : 'green',
                        }}
                      >
                        {info1.sinr.toFixed(2)}
                      </b>
                    </div>
                  )}
                  {info2 && (
                    <div style={{ borderTop: '1px solid white' }}>
                      <b
                        style={{
                          color:
                            info2.rsrpLevel === 'POOR'
                              ? 'red'
                              : info2.rsrpLevel === 'FAIR'
                                ? 'orange'
                                : 'green',
                        }}
                      >
                        {info2.rsrp.toFixed(2)}
                      </b>
                      &nbsp;
                      <b
                        style={{
                          color:
                            info2.sinrLevel === 'POOR'
                              ? 'red'
                              : info2.sinrLevel === 'FAIR'
                                ? 'orange'
                                : 'green',
                        }}
                      >
                        {info2.sinr.toFixed(2)}
                      </b>
                    </div>
                  )}
                </div>
              </Tooltip>
            ) : (
              '--'
            )
          }
        />
        <FieldItem
          title={
            <div className="mqtt-title">
              座舱
              <QuestionCircleFilled style={{ fontSize: '14px' }} />
            </div>
          }
          info={
            <Tooltip
              title={mqttTip}
              placement="top"
              color="rgb(35 34 34 85%)"
              overlayStyle={{ maxWidth: 900, whiteSpace: 'normal' }}
            >
              <div className="mqtt-info">
                {driveInMqtt1Online != null && driveOutMqtt1Online != null ? (
                  <div>
                    <span
                      style={{
                        color: driveInMqtt1Online ? 'green' : 'red',
                      }}
                    >
                      {driveInMqtt1Online ? '在线' : '离线'}
                    </span>
                    &nbsp;
                    <span
                      style={{
                        color: driveOutMqtt1Online ? 'green' : 'red',
                      }}
                    >
                      {driveOutMqtt1Online ? '在线' : '离线'}
                    </span>
                  </div>
                ) : (
                  <div>--</div>
                )}
                {driveInMqtt2Online != null && driveOutMqtt2Online != null ? (
                  <div style={{ borderTop: '1px solid white' }}>
                    <span
                      style={{
                        color: driveInMqtt2Online ? 'green' : 'red',
                      }}
                    >
                      {driveInMqtt2Online ? '在线' : '离线'}
                    </span>
                    &nbsp;
                    <span
                      style={{
                        color: driveOutMqtt2Online ? 'green' : 'red',
                      }}
                    >
                      {driveOutMqtt2Online ? '在线' : '离线'}
                    </span>
                  </div>
                ) : (
                  <div style={{ borderTop: '1px solid white' }}> --</div>
                )}
              </div>
            </Tooltip>
          }
        />
        <FieldItem
          title="里程"
          info={
            !arrivedMileage && !globalMileage
              ? '--'
              : `${arrivedMileage?.toFixed(1) || '--'}/${globalMileage?.toFixed(
                  1
                )}`
          }
        />
        {cockpitMode === CockpitMode.MAP_COLLECTION_MODE ? (
          <FieldItem
            title="绑定状态"
            info={bindingState ? '已绑定' : '未绑定'}
          />
        ) : (
          <FieldItem
            title="配送状态"
            info={translatorEnum('SCHEDULE_STATE_ENUM', scheduleState) ?? '--'}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(StatusFields);
